// Shell-level Stream Chat session (WC-V20-HOST).
// One connectUser per org-scoped workspace mount; UI hosts must not disconnect.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { StreamChat } from "stream-chat";
import { useAuth } from "@/contexts/AuthContext";
import { useMessageAlertDing } from "@/components/messages/useMessageAlertDing";
import { useStreamMuteAwareUnread } from "@/hooks/useStreamMuteAwareUnread";
import {
  createWorkplaceChatDm,
  createWorkplaceChatOrgChannel,
  deleteWorkplaceChatOrgChannel,
  getWorkplaceChatStatus,
  getWorkplaceChatToken,
  patchWorkplaceChatBridgeMute,
  syncWorkplaceChatBridge,
  type WorkplaceChatTokenPayload,
} from "@/lib/workplaceChatClient";

export type WorkplaceStreamChatSessionValue = {
  vendorEnabled: boolean | null;
  ready: boolean;
  useFallback: boolean;
  fallbackReason: string | null;
  loading: boolean;
  errorText: string;
  client: StreamChat | null;
  tokenPayload: WorkplaceChatTokenPayload | null;
  displayName: string;
  unreadCount: number;
  orgChannels: string[];
  bridgeChannelSlugs: string[];
  mutedChannelIds: string[];
  startDm: (recipientUserIds: string[]) => Promise<{ ok: boolean; error?: string }>;
  createOrgChannel: (input: {
    slug: string;
    display_name?: string;
    member_user_ids?: string[];
  }) => Promise<{ ok: boolean; error?: string; org_channels?: string[] }>;
  deleteOrgChannel: (slug: string) => Promise<{ ok: boolean; error?: string; org_channels?: string[] }>;
  setBridgeMuted: (channelSlug: string, muted: boolean) => Promise<void>;
  reload: () => void;
};

const WorkplaceStreamChatSessionContext = createContext<WorkplaceStreamChatSessionValue | null>(
  null
);

const EMPTY: WorkplaceStreamChatSessionValue = {
  vendorEnabled: null,
  ready: false,
  useFallback: false,
  fallbackReason: null,
  loading: false,
  errorText: "",
  client: null,
  tokenPayload: null,
  displayName: "User",
  unreadCount: 0,
  orgChannels: [],
  bridgeChannelSlugs: [],
  mutedChannelIds: [],
  startDm: async () => ({ ok: false, error: "Messenger session unavailable" }),
  createOrgChannel: async () => ({ ok: false, error: "Messenger session unavailable" }),
  deleteOrgChannel: async () => ({ ok: false, error: "Messenger session unavailable" }),
  setBridgeMuted: async () => undefined,
  reload: () => undefined,
};

export function useWorkplaceStreamChatSession(): WorkplaceStreamChatSessionValue {
  return useContext(WorkplaceStreamChatSessionContext) ?? EMPTY;
}

export function WorkplaceStreamChatSessionProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const displayName =
    auth.profile?.full_name?.trim() ||
    auth.session?.user?.email?.trim() ||
    "User";

  const [vendorEnabled, setVendorEnabled] = useState<boolean | null>(null);
  const [ready, setReady] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  const [fallbackReason, setFallbackReason] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [bridgeChannelSlugs, setBridgeChannelSlugs] = useState<string[]>([]);
  const [orgChannels, setOrgChannels] = useState<string[]>([]);
  const [mutedChannelIds, setMutedChannelIds] = useState<string[]>([]);
  const [tokenPayload, setTokenPayload] = useState<WorkplaceChatTokenPayload | null>(null);
  const [client, setClient] = useState<StreamChat | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  const unreadCount = useStreamMuteAwareUnread(client, mutedChannelIds);
  // HOST-4 — ding + optional desktop toast on mute-aware unread increases.
  useMessageAlertDing(unreadCount);

  useEffect(() => {
    let cancelled = false;
    let chatClient: StreamChat | null = null;

    (async () => {
      setLoading(true);
      setErrorText("");
      const statusRes = await getWorkplaceChatStatus();
      if (cancelled) return;
      if (!statusRes.ok || !statusRes.data?.enabled) {
        setVendorEnabled(false);
        setReady(false);
        setUseFallback(false);
        setTokenPayload(null);
        setClient(null);
        setLoading(false);
        return;
      }
      setVendorEnabled(true);
      setBridgeChannelSlugs(statusRes.data.bridge_channel_slugs ?? []);
      setOrgChannels(statusRes.data.org_channels ?? []);
      setMutedChannelIds(statusRes.data.bridge_muted_channel_ids ?? []);
      if (statusRes.data.bridge_enabled) {
        void syncWorkplaceChatBridge();
      }
      if (statusRes.data.fallback_reason) {
        setUseFallback(true);
        setFallbackReason(statusRes.data.fallback_reason);
        setReady(false);
        setLoading(false);
        return;
      }

      const tokenRes = await getWorkplaceChatToken();
      if (cancelled) return;
      if (!tokenRes.ok) {
        setUseFallback(true);
        setFallbackReason("token_failed");
        setReady(false);
        setErrorText(tokenRes.error || "Unable to connect to team chat.");
        setLoading(false);
        return;
      }
      const payload = tokenRes.data;
      const streamApiKey = payload.stream_api_key?.trim() ?? "";
      if (!payload.token || !streamApiKey) {
        setUseFallback(true);
        setFallbackReason("token_failed");
        setReady(false);
        setErrorText("Unable to connect to team chat.");
        setLoading(false);
        return;
      }
      chatClient = StreamChat.getInstance(streamApiKey);
      try {
        const teams =
          Array.isArray(payload.org_ids) && payload.org_ids.length > 0
            ? payload.org_ids
            : [payload.org_id];
        await chatClient.connectUser(
          { id: payload.user_id, name: displayName, teams },
          payload.token
        );
      } catch (err) {
        if (cancelled) return;
        setUseFallback(true);
        setFallbackReason("connect_failed");
        setReady(false);
        setErrorText(err instanceof Error ? err.message : "Stream connect failed");
        setLoading(false);
        return;
      }
      if (cancelled) {
        void chatClient.disconnectUser();
        return;
      }

      setTokenPayload(payload);
      setClient(chatClient);
      setUseFallback(false);
      setFallbackReason(null);
      setReady(true);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
      setClient(null);
      setReady(false);
      if (chatClient) {
        void chatClient.disconnectUser();
      }
    };
  }, [displayName, reloadKey]);

  const startDm = useCallback(async (recipientUserIds: string[]) => {
    const r = await createWorkplaceChatDm(recipientUserIds);
    if (!r.ok) return { ok: false, error: r.error || "Policy blocked or server error" };
    return { ok: true };
  }, []);

  const createOrgChannel = useCallback(
    async (input: { slug: string; display_name?: string; member_user_ids?: string[] }) => {
      const r = await createWorkplaceChatOrgChannel(input);
      if (!r.ok) return { ok: false, error: r.error || "Could not create channel" };
      if (r.data.org_channels) setOrgChannels(r.data.org_channels);
      return { ok: true, org_channels: r.data.org_channels };
    },
    [],
  );

  const deleteOrgChannel = useCallback(async (slug: string) => {
    const r = await deleteWorkplaceChatOrgChannel(slug);
    if (!r.ok) return { ok: false, error: r.error || "Could not delete channel" };
    if (r.data.org_channels) setOrgChannels(r.data.org_channels);
    return { ok: true, org_channels: r.data.org_channels };
  }, []);

  const setBridgeMuted = useCallback(async (channelSlug: string, muted: boolean) => {
    const r = await patchWorkplaceChatBridgeMute({ channel_slug: channelSlug, muted });
    if (r.ok && r.data) setMutedChannelIds(r.data.bridge_muted_channel_ids ?? []);
  }, []);

  const value = useMemo<WorkplaceStreamChatSessionValue>(
    () => ({
      vendorEnabled,
      ready,
      useFallback,
      fallbackReason,
      loading,
      errorText,
      client,
      tokenPayload,
      displayName,
      unreadCount,
      orgChannels,
      bridgeChannelSlugs,
      mutedChannelIds,
      startDm,
      createOrgChannel,
      deleteOrgChannel,
      setBridgeMuted,
      reload,
    }),
    [
      vendorEnabled,
      ready,
      useFallback,
      fallbackReason,
      loading,
      errorText,
      client,
      tokenPayload,
      displayName,
      unreadCount,
      orgChannels,
      bridgeChannelSlugs,
      mutedChannelIds,
      startDm,
      createOrgChannel,
      deleteOrgChannel,
      setBridgeMuted,
      reload,
    ]
  );

  return (
    <WorkplaceStreamChatSessionContext.Provider value={value}>
      {children}
    </WorkplaceStreamChatSessionContext.Provider>
  );
}
